import type { Translate } from './i18n';
import type { MovieDetail } from './types';
import type { EpisodeDetail, EpisodeItem, EpisodePlayback } from './series-types';

type MessageKey = Parameters<Translate>[0];

export const EPISODE_STATUS_KEYS = {
  ready: 'seriesStatusReady',
  collected: 'seriesCollected',
  processing: 'seriesStatusProcessing',
  downloading: 'seriesStatusDownloading',
  queued: 'seriesStatusQueued',
  searching: 'seriesStatusSearching',
  unavailable: 'seriesStatusUnavailable',
  upcoming: 'seriesUpcoming',
  not_requested: 'seriesStatusNotRequested',
} as const satisfies Record<string, MessageKey>;

const MISSING_REASON_KEYS = {
  no_source: 'seriesNoSource',
  source_failed: 'seriesSourceFailed',
  rejected: 'seriesRejected',
} as const satisfies Record<string, MessageKey>;

/** Statuses an episode can be ordered from: nothing is on its way and it has aired. */
export const ORDERABLE_STATUSES = new Set(['not_requested', 'unavailable', 'searching']);

/** Statuses with work in progress: the season is watched live (WS, or polling without it). */
export const ACTIVE_STATUSES = new Set(['queued', 'searching', 'downloading', 'processing', 'packaging', 'collected']);

/** A TMDb calendar date (YYYY-MM-DD) in the viewer's locale; the day never shifts with the time zone. */
export function formatAirDate(date: string | null | undefined, localeTag: string): string {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return date ?? '';
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(localeTag, { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
}

export function episodeStatusLabel(status: string, t: Translate): string {
  return status in EPISODE_STATUS_KEYS ? t(EPISODE_STATUS_KEYS[status as keyof typeof EPISODE_STATUS_KEYS]) : status;
}

/** The status line of an episode: state, progress of work, why it is missing and the next attempt. */
export function episodeStatusText(availability: EpisodeItem['availability'], t: Translate, localeTag: string): string {
  const parts = [episodeStatusLabel(availability.status, t)];
  const progress = availability.progress ?? availability.displayProgress;
  if ((availability.status === 'downloading' || availability.status === 'processing') && typeof progress === 'number' && progress > 0) {
    parts[0] += ` ${Math.round(progress)}%`;
  }
  const reason = availability.missingReason;
  if (reason && reason in MISSING_REASON_KEYS) parts.push(t(MISSING_REASON_KEYS[reason as keyof typeof MISSING_REASON_KEYS]));
  if (availability.nextSearchAt) {
    parts.push(t('seriesNextSearch', { date: new Date(availability.nextSearchAt).toLocaleString(localeTag, { dateStyle: 'medium', timeStyle: 'short' }) }));
  }
  return parts.join(' · ');
}

/** Watched time, or "watched" once the server marked the episode completed. */
export function episodeProgressLabel(progress: EpisodeItem['progress'], t: Translate): string | null {
  if (!progress) return null;
  if (progress.completed) return t('watched');
  if (progress.currentTime <= 0) return null;
  return t('minutesWatched', { count: Math.max(1, Math.round(progress.currentTime / 60)) });
}

/** The film player's input for an episode: both endpoints share one playback contract. */
export function episodePlayerItem(item: EpisodeDetail, media: EpisodePlayback): MovieDetail {
  return {
    id: item.id, slug: null, year: null, href: '',
    title: `${item.seriesTitle} · S${item.seasonNumber}E${item.episodeNumber}${item.title ? ` · ${item.title}` : ''}`,
    poster: item.still, backdrop: item.backdrop, description: item.overview,
    durationSec: media.durationSec, runtime: item.runtime, video: null, playback: media.playback,
    audio: media.audio, subtitles: media.subtitles,
    firstFrameTelemetry: media.firstFrameTelemetry,
    availability: { status: 'ready', label: 'Ready', progress: 100, playbackReady: true, availableAt: null, availableInSec: null },
  };
}
